import React, { useState, useEffect } from 'react'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import Icon from '../shared/icon'
import { t } from '../../i18n'
import { Slot, SlotState, SlotType, SLOT_TYPES_WITH_IS_FAULTY } from './kitchenConfigurationHelper'
import Tooltip from '@mui/material/Tooltip'

const scope = 'locations.kitchen_configuration'

interface KitchenGridProps {
  activeFields: Slot[];
  setActiveFields: React.Dispatch<React.SetStateAction<Slot[]>>;
  slotType: SlotType;
  fridgeColumns?: number;
  kitchenConfigurationOptions: {};
  kitchenConfigurationErrors: {};
  setKitchenConfigurationErrors: React.Dispatch<React.SetStateAction<{}>>;
  disableAccordion?: boolean;
  onSlotAlertsClick?: (slot: Slot) => void;
  hideTravelTimeInput?: boolean;
}

const KitchenConfigurationGrid: React.FC<KitchenGridProps> = ({
  slotType,
  activeFields,
  setActiveFields,
  kitchenConfigurationOptions,
  setKitchenConfigurationErrors,
  kitchenConfigurationErrors,
  fridgeColumns = 0,
  disableAccordion = false,
  onSlotAlertsClick,
  hideTravelTimeInput = false,
 }) => {

  const rows = kitchenConfigurationOptions[slotType].rows;
  const columns = kitchenConfigurationOptions[slotType].columns;

  const [selectAll, setSelectAll] = useState<boolean>(false);

  useEffect(() => {
    handleConfigurationErrors()
  }, [activeFields, slotType])

  const handleSelectAllChange = () => {
    setSelectAll(prevSelectAll => {
      const newSelectAll = !prevSelectAll;
      if(newSelectAll) {
        const newActiveFields = activeFields.map(field => ({
          ...field,
          state: newSelectAll ? SlotState.ENABLED : SlotState.DISABLED,
        }));
        setActiveFields(newActiveFields);
      }
      return newSelectAll;
    });
  };

  const handleCheckboxChange = (index: number) => {
    const newActiveFields = [...activeFields];
    newActiveFields[index] = {
      ...newActiveFields[index],
      state: newActiveFields[index].state === SlotState.ENABLED ? SlotState.DISABLED : SlotState.ENABLED,
    };
    setActiveFields(newActiveFields);
  };

  const handleTravelTimeInputChange = (index: number, value: number) => {
    const newActiveFields = [...activeFields];
    newActiveFields[index] = {
      ...newActiveFields[index],
      default_travel_time_seconds: value,
    };
    setActiveFields(newActiveFields);
  }

  const handleFaultyToggle = (index: number) => {
    const newActiveFields = [...activeFields];
    newActiveFields[index] = {
      ...newActiveFields[index],
      is_faulty: !newActiveFields[index].is_faulty,
    };
    setActiveFields(newActiveFields);
  }

  const getSlotStateClass = (slot: Slot, isFridgeSlot: boolean) => {
    const classes: string[] = [];
    if (slot?.state === SlotState.DISABLED) {
      classes.push('slot-disabled');
    } else {
      classes.push('slot-enabled');
      if (slot?.is_faulty) {
        classes.push('slot-faulty');
      }
    }
    if (isFridgeSlot) {
      classes.push('fridge-slot');
    }
    return classes.join(' ');
  }

  const PLATE_STANDS = ['C', 'D']

  const getHumanSlotNumber = (index: number) => {
    switch (slotType) {
      case SlotType.FIXED_CONTAINER:
        return `A${index + 1}`
      case SlotType.MOBILE_CONTAINER:
        return `B${index + 1}`
      case SlotType.PLATE_STAND:
        return PLATE_STANDS[index]
      case SlotType.LOCKER:
        return `L${index + 1}`
      case SlotType.ROBOT_TRAY:
        return `T${index + 1}`
      default:
        return `${index + 1}`
    }
  };

  const validateSelectedSlots = () => {
    if(noFridgeColumns()) {
      return noFieldsSelected() ? t('validations.slots', { scope }) : null
    } else {
      const fridgeSlotsSelected = fridgeSlots()
        .filter(field => field.state === SlotState.ENABLED).length
      const nonFridgeSlotsSelected = nonFridgeSlots()
        .filter(field => field.state === SlotState.ENABLED).length
      if(fridgeSlotsSelected == 0 || nonFridgeSlotsSelected == 0) {
        return t('validations.fridge_slots', { scope })
      }
    }
    return null
  }

  const handleConfigurationErrors = () => {
    const errors = validateSelectedSlots();
      setKitchenConfigurationErrors({...kitchenConfigurationErrors, [slotType]: errors != null})
  };

  const noFieldsSelected = () => {
    return activeFields.filter(field => field.state === SlotState.ENABLED).length == 0
  }

  const noFridgeColumns = () => {
    return fridgeColumns == 0
  }

  const nonFridgeSlots = () => {
    return activeFields.filter((field, index) => {
      const col = index % columns
      return col >= fridgeColumns
    });
  }

  const fridgeSlots = () => {
    return activeFields.filter((field, index) => {
      const col = index % columns
      return col < fridgeColumns
    });
  }
  const productContainerSlots = () => {
    return slotType == SlotType.FIXED_CONTAINER || slotType == SlotType.MOBILE_CONTAINER
  }

  const renderGrid = () => {
    const grid: JSX.Element[] = [];
    for (let row = 0; row < rows; row++) {
      const rowItems: JSX.Element[] = [];
      for (let col = 0; col < columns; col++) {
        const index = row * columns + col;
        const isFridgeSlot = fridgeColumns > 0 && col < fridgeColumns;
        const slot = activeFields[index];
        const stateClass = getSlotStateClass(slot, isFridgeSlot);

        const showFaultyToggle = SLOT_TYPES_WITH_IS_FAULTY.includes(slotType)
        const showAlertsButton = Boolean(onSlotAlertsClick && slot)
        const showTravelTime = productContainerSlots() && !hideTravelTimeInput

        rowItems.push(
          <div
            key={index}
            id={`slot-${getHumanSlotNumber(index)}`}
            className={`grid-item-large ${stateClass}`}
          >
            <div className="slot-icon-row">
              <Tooltip title={slot?.state === SlotState.ENABLED ? t('disable', { scope }) : t('enable', { scope })} placement="top" arrow>
                <input
                  type="checkbox"
                  id={`slot-${getHumanSlotNumber(index)}-checkbox`}
                  checked={slot?.state === SlotState.ENABLED}
                  onChange={() => handleCheckboxChange(index)}
                  disabled={selectAll}
                  className="enabled-checkbox"
                />
              </Tooltip>
              {showFaultyToggle && (
                <Tooltip title={slot?.is_faulty ? t('mark_not_faulty', { scope }) : t('mark_faulty', { scope })} placement="top" arrow>
                  <span className="faulty-toggle-wrapper">
                    <button
                      type="button"
                      onClick={() => handleFaultyToggle(index)}
                      className={`faulty-toggle ${slot?.is_faulty ? 'is-faulty' : ''}`}
                      disabled={slot?.state === SlotState.DISABLED}
                    >
                      <Icon name="warning" color={slot?.is_faulty ? '#e65100' : '#bbb'} />
                    </button>
                  </span>
                </Tooltip>
              )}
              {showAlertsButton && (
                <Tooltip title={t('show_alerts', { scope })} placement="top" arrow>
                  <button
                    type="button"
                    onClick={() => onSlotAlertsClick!(slot!)}
                    className="alerts-toggle"
                    aria-label={t('show_alerts', { scope })}
                  >
                    <i className="bi bi-bell" aria-hidden="true" />
                  </button>
                </Tooltip>
              )}
            </div>
            <div className="slot-label-wrapper">
              <span className="slot-label"><strong>{getHumanSlotNumber(index)}</strong></span>
            </div>
            {showTravelTime && (
              <Tooltip title={t('travel_time', { scope })} placement="bottom" arrow>
                <input
                  type="number"
                  min={0}
                  autoComplete="off"
                  id={`slot-${getHumanSlotNumber(index)}-travel-time`}
                  value={slot?.default_travel_time_seconds || 0}
                  onChange={(e) => handleTravelTimeInputChange(index, Number(e.target.value))}
                  disabled={slot?.state === SlotState.DISABLED || selectAll}
                />
              </Tooltip>
            )}
          </div>
        );
      }
      grid.push(
        <div key={row} className="grid-row">
          {rowItems}
        </div>
      );
    }
    return grid;
  };

  const renderErrors = () => {
    const errors = validateSelectedSlots()
    if(errors) {
      return (
        <div className="error-message">
          {errors}
        </div>
      )
    } else {
      return null
    }
  }

  const body = (
    <div className="kitchen-grid-container">
      <div className="select-all">
        <FormControlLabel
          control={
            <Checkbox
              checked={selectAll}
              onChange={handleSelectAllChange}
              sx={{
                color: 'black',
                '&.Mui-checked': {
                  color: 'black',
                },
              }}
            />
          }
          label="Select All"
        />
      </div>
      <div className="kitchen-grid">
        {renderGrid()}
      </div>
      <div className="kitchen-grid-errors">{renderErrors()}</div>
    </div>
  );

  if (disableAccordion) {
    return (
      <div id={`${slotType}-configuration`}>
        {body}
      </div>
    );
  }

  return (
    <Accordion id={`${slotType}-configuration`}>
      <AccordionSummary expandIcon={<Icon name='arrowDown' color='white'/>}>
        <Typography>{t(slotType, { scope })}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {body}
      </AccordionDetails>
    </Accordion>
  );
};

export default KitchenConfigurationGrid;
